import { ETaskStatus } from '@constants';
import { ITask } from '@sdk/contracts';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { TaskItem } from './TaskItem';
import { TitleWrapper, Wrapper } from './TaskList.styles';

import { useCourseTasks } from '@/store';

type Props = {
  tasks: ITask[];
  status: ETaskStatus;
  onScroll: (
    ref: React.RefObject<HTMLDivElement | null>,
    isFetchingNextPage: boolean,
    fetchNextPage: () => void
  ) => void;
};

export const TaskList: React.FC<Props> = ({ tasks, status, onScroll }) => {
  const { t } = useTranslation('CourseTranslations');
  const { colors } = useTheme();
  const tasksListRef = useRef<HTMLDivElement>(null);
  const {
    isFetchingNextPagePendingAndRejected,
    fetchNextPagePendingAndRejected,
    isFetchingNextPageReview,
    fetchNextPageReview,
    isFetchingNextPageApproved,
    fetchNextPageApproved
  } = useCourseTasks();

  const isFetchingNextPage = useMemo(() => {
    switch (status) {
      case ETaskStatus.PENDING:
        return isFetchingNextPagePendingAndRejected;
      case ETaskStatus.IN_REVIEW:
        return isFetchingNextPageReview;
      case ETaskStatus.APPROVED:
        return isFetchingNextPageApproved;
      default:
        return false;
    }
  }, [
    status,
    isFetchingNextPagePendingAndRejected,
    isFetchingNextPageReview,
    isFetchingNextPageApproved
  ]);

  const fetchNextPage = useMemo(() => {
    switch (status) {
      case ETaskStatus.PENDING:
        return fetchNextPagePendingAndRejected;
      case ETaskStatus.IN_REVIEW:
        return fetchNextPageReview;
      case ETaskStatus.APPROVED:
        return fetchNextPageApproved;
      default:
        return () => {};
    }
  }, [status, fetchNextPagePendingAndRejected, fetchNextPageReview, fetchNextPageApproved]);

  const backgroundColor = useMemo(() => {
    switch (status) {
      case ETaskStatus.PENDING:
        return colors.black2;
      case ETaskStatus.IN_REVIEW:
        return 'rgba(234, 164, 75, 0.15)';
      case ETaskStatus.APPROVED:
        return colors.green1;
      default:
        return '#F7F7F7';
    }
  }, [status]);

  return (
    <Wrapper
      color={backgroundColor}
      ref={tasksListRef}
      onScroll={() => onScroll(tasksListRef, isFetchingNextPage, fetchNextPage)}>
      <TitleWrapper>{t(status).toLocaleUpperCase()}</TitleWrapper>
      {tasks.map(task => (
        <TaskItem key={task._id} task={task} />
      ))}
    </Wrapper>
  );
};
